/**
 * InstructorDetail 컴포넌트 - 강사 상세 정보 페이지
 */

import { useQueryClient, useMutation } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import React, { useState } from 'react';
import { HiTrash, HiPencil } from 'react-icons/hi';
import { useParams, useNavigate } from 'react-router-dom';
import { deleteInstructor } from '../../api/instructorApi';
import { DetailPageLayout } from '../../components/Layout/DetailPageLayout';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { InfoItem } from '../../components/ui/InfoItem';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useInstructorDetail } from '../../hooks/useInstructorDetail';

/**
 * 이미지 URL을 절대 경로로 변환하는 헬퍼 함수
 * 상대 경로인 경우 API base URL을 붙임
 */
const getImageUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;

  // 이미 절대 경로인 경우 그대로 반환
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  // 상대 경로인 경우 API base URL을 붙임
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4010';
  // URL이 /로 시작하지 않으면 /를 추가
  const normalizedUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_URL}${normalizedUrl}`;
};

const InstructorDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const instructorId = id ? parseInt(id, 10) : 0;

  const { data: instructor, isLoading, error } = useInstructorDetail(instructorId);

  // 삭제 확인 모달 상태
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  // 삭제 뮤테이션
  const deleteMutation = useMutation({
    mutationFn: async (instructorId: number) => {
      await deleteInstructor(instructorId);
    },
    onSuccess: async () => {
      // 강사 목록 캐시 무효화
      await queryClient.invalidateQueries({ queryKey: ['instructors'] });
      // 강의 목록 캐시도 무효화 (강사 정보가 포함되어 있을 수 있음)
      await queryClient.invalidateQueries({ queryKey: ['courses'] });
      // 강의 상세 캐시도 무효화
      await queryClient.invalidateQueries({ queryKey: ['course'] });
      // 강사 상세 캐시 제거
      await queryClient.removeQueries({ queryKey: ['instructor', instructorId] });
      // 강사 목록으로 이동
      navigate('/dashboard/instructors');
    },
    onError: (error) => {
      console.error('강사 삭제 실패:', error);
      alert('강사 삭제 중 오류가 발생했습니다.');
    },
  });

  // 삭제 핸들러
  const handleDeleteClick = () => {
    if (!instructor) return;

    setConfirmModal({
      isOpen: true,
      title: '강사 삭제',
      message: `정말로 "${instructor.name}" 강사를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.`,
      onConfirm: () => {
        deleteMutation.mutate(instructorId);
        setConfirmModal({ ...confirmModal, isOpen: false });
      },
    });
  };

  // 편집 핸들러
  const handleEditClick = () => {
    navigate(`/dashboard/instructors/${instructorId}/edit`);
  };

  // 이미지 로드 에러 상태
  const [imageError, setImageError] = useState(false);

  if (isLoading) {
    return (
      <DetailPageLayout title="강사 상세" onBack={() => navigate('/dashboard/instructors')}>
        <div className="flex h-64 items-center justify-center">
          <LoadingSpinner />
        </div>
      </DetailPageLayout>
    );
  }

  if (error || !instructor) {
    return (
      <DetailPageLayout title="강사 상세" onBack={() => navigate('/dashboard/instructors')}>
        <div className="flex h-64 flex-col items-center justify-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="mb-2 text-lg font-bold text-slate-900">오류가 발생했습니다</h3>
          <p className="mb-6 text-slate-500">
            {error instanceof Error ? error.message : '강사 정보를 불러오는데 실패했습니다.'}
          </p>
          <Button onClick={() => navigate('/dashboard/instructors')}>목록으로 돌아가기</Button>
        </div>
      </DetailPageLayout>
    );
  }

  const imageUrl = getImageUrl(instructor.imageUrl);

  return (
    <DetailPageLayout
      title="강사 상세"
      onBack={() => navigate('/dashboard/instructors')}
      actions={
        <>
          <Button variant="outline" leftIcon={<HiPencil />} onClick={handleEditClick}>
            편집
          </Button>
          <Button
            variant="danger"
            leftIcon={<HiTrash />}
            onClick={handleDeleteClick}
            disabled={deleteMutation.isPending}>
            삭제
          </Button>
        </>
      }>
      <div className="space-y-6">
        {/* 기본 정보 카드 */}
        <Card title="기본 정보">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <InfoItem label="이름" value={instructor.name} />
            <InfoItem label="직업" value={instructor.occupation || '-'} />
            <InfoItem
              label="상태"
              value={<Badge variant={instructor.state === 'NORMAL' ? 'success' : 'neutral'}>{instructor.state}</Badge>}
            />
            <InfoItem label="생성일" value={new Date(instructor.createdAt).toLocaleDateString('ko-KR')} />
            <InfoItem label="수정일" value={new Date(instructor.updatedAt).toLocaleDateString('ko-KR')} />
          </div>
        </Card>

        {/* 프로필 이미지 및 소개 카드 */}
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <Card title="프로필 이미지">
            <div className="flex justify-center">
              {imageUrl && !imageError ? (
                <img
                  src={imageUrl}
                  alt={instructor.name}
                  className="h-40 w-40 rounded-full border-2 border-slate-200 object-cover"
                  onError={() => {
                    console.error('강사 프로필 이미지 로드 실패:', {
                      imageUrl: instructor.imageUrl,
                      resolvedUrl: imageUrl,
                    });
                    setImageError(true);
                  }}
                  onLoad={() => {
                    console.log('강사 프로필 이미지 로드 성공:', {
                      originalUrl: instructor.imageUrl,
                      resolvedUrl: imageUrl,
                    });
                  }}
                />
              ) : (
                <div className="flex h-40 w-40 items-center justify-center rounded-full border-2 border-slate-200 bg-indigo-100 text-4xl font-medium text-indigo-600">
                  {instructor.name.charAt(0)}
                </div>
              )}
            </div>
          </Card>

          <Card title="소개 정보">
            <div className="space-y-4">
              <InfoItem label="소개 제목" value={instructor.bioTitle || '-'} />
              {instructor.bioContent && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700">소개 내용</label>
                  <div
                    className="prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(instructor.bioContent),
                    }}
                  />
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>

      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
        title={confirmModal.title}
        message={confirmModal.message}
        type="warning"
        buttons={[
          {
            label: '취소',
            onClick: () => setConfirmModal({ ...confirmModal, isOpen: false }),
            variant: 'secondary',
          },
          {
            label: '삭제',
            onClick: confirmModal.onConfirm,
            variant: 'danger',
            isLoading: deleteMutation.isPending,
          },
        ]}
      />
    </DetailPageLayout>
  );
};

export default InstructorDetail;
